import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./expenses.css";
import "../finance/finance.css";
import { fmt } from "../finance/finance";

const formatBreakdown = (breakdown) =>
    (breakdown || []).map((b) => {
        const symbol = b.currency === "USD" ? "$" : "";
        const suffix = b.currency !== "USD" ? ` ${b.currency}` : "";
        return `${symbol}${fmt(b.amount)}${suffix}`;
    }).join(" · ");

const formatDate = (d) => {
    if (!d) return "";
    const s = String(d).slice(0, 10);
    const parts = s.split("-");
    if (parts.length !== 3) return s;
    const [y, m, day] = parts;
    return `${day}.${m}.${y}`;
};

const roleLabel = (e) => {
    if (e.isPlanned) return "План";
    if (e.role === "cafe") return "Кафе";
    if (e.role === "driver" || e.role === "guide") return "Зарплаты";
    return "Расход";
};

let entryUid = 0;
const nextEntryId = () => ++entryUid;

const ExpenseRow = ({ e, overdue, postponed, API_URL, paymentTypes = [], kassaList = [], onSaved }) => {
    const [open, setOpen] = useState(false);
    const [entries, setEntries] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [postponing, setPostponing] = useState(false);

    const isPaid = e.paidBreakdown && e.paidBreakdown.length > 0;

    const openPanel = () => {
        setEntries([{ id: nextEntryId(), paymentTypeId: paymentTypes[0]?.id || null, kassaId: null, amount: "" }]);
        setOpen(true);
    };

    const addEntry = () => {
        setEntries((prev) => [...prev, { id: nextEntryId(), paymentTypeId: paymentTypes[0]?.id || null, kassaId: null, amount: "" }]);
    };

    const removeEntry = (id) => {
        setEntries((prev) => (prev.length > 1 ? prev.filter((en) => en.id !== id) : prev));
    };

    const updateEntry = (id, patch) => {
        setEntries((prev) => prev.map((en) => (en.id === id ? { ...en, ...patch } : en)));
    };

    const submit = async () => {
        for (const en of entries) {
            const st = paymentTypes.find((pt) => pt.id === en.paymentTypeId);
            if (!en.paymentTypeId) { toast.error("Выберите тип оплаты во всех строках"); return; }
            if (st?.isCard && !en.kassaId) { toast.error("Выберите карту во всех строках"); return; }
            if (!en.amount || Number(en.amount) <= 0) { toast.error("Укажите сумму во всех строках"); return; }
        }
        setSubmitting(true);
        try {
            for (const en of entries) {
                const st = paymentTypes.find((pt) => pt.id === en.paymentTypeId);
                await axios.post(`${API_URL}/addExpensePayment`, {
                    expenseId: e.expenseId || null,
                    title: e.title,
                    orderNumber: e.orderNumber, requestType: e.requestType, role: e.role, personName: e.personName,
                    paymentTypeId: en.paymentTypeId, kassaId: st?.isCard ? en.kassaId : null,
                    amount: en.amount,
                });
            }
            toast.success(entries.length > 1 ? "Оплаты сохранены" : "Оплата сохранена");
            setOpen(false);
            onSaved();
        } catch (err) {
            console.error("addExpensePayment:", err);
            toast.error("Не удалось сохранить — проверьте список");
        } finally {
            setSubmitting(false);
        }
    };

    const postponingRef = useRef(false);

    const postpone = async () => {
        if (postponingRef.current) return;
        postponingRef.current = true;
        setPostponing(true);
        try {
            await axios.post(`${API_URL}/postponeExpense`, {
                expenseId: e.expenseId || null,
                title: e.title,
                orderNumber: e.orderNumber, requestType: e.requestType, role: e.role, personName: e.personName,
            });
            toast.success("Отложено");
            onSaved();
        } catch (err) {
            console.error("postponeExpense:", err);
            toast.error("Не удалось отложить");
        } finally {
            postponingRef.current = false;
            setPostponing(false);
        }
    };

    const unpostpone = async () => {
        setPostponing(true);
        try {
            await axios.post(`${API_URL}/unpostponeExpense`, { expenseId: e.expenseId });
            toast.success("Возвращено в работу");
            onSaved();
        } catch (err) {
            console.error("unpostponeExpense:", err);
            toast.error("Не удалось вернуть");
        } finally {
            setPostponing(false);
        }
    };

    const rowClass = `ex-row${isPaid ? " paid" : postponed ? " postponed" : overdue ? " overdue" : ""}`;

    return (
        <div className={rowClass}>
            <div className="ex-top">
                <div className="ex-info">
                    <div className="ex-title">{e.title}</div>
                    <div className="ex-sub">{roleLabel(e)}</div>
                    {e.dateEnd && <div className="ex-date">Тур: {formatDate(e.dateEnd)}</div>}
                    {e.isPlanned && (
                        <div className="ex-planned-amount">
                            Ожидаемая сумма: {e.plannedCurrency === "USD" ? "$" : ""}{fmt(e.plannedAmount)}{e.plannedCurrency !== "USD" ? ` ${e.plannedCurrency}` : ""}
                        </div>
                    )}
                </div>
                {isPaid ? (
                    <>
                        <span className="fn-pill paid">Оплачено</span>
                        <div className="ex-amount">{formatBreakdown(e.paidBreakdown)}</div>
                    </>
                ) : (
                    <span className="fn-pill unpaid">Ожидает оплаты</span>
                )}
            </div>

            <div className="ex-actions">
                {!open && (
                    <>
                        <button className="btn-expense" onClick={openPanel}>Оплатить</button>
                        {postponed ? (
                            <button className="btn-expense-outline" onClick={unpostpone} disabled={postponing}>
                                {postponing ? "…" : "Вернуть в работу"}
                            </button>
                        ) : (
                            <button className="btn-expense-outline" onClick={postpone} disabled={postponing}>
                                {postponing ? "…" : "Отложить"}
                            </button>
                        )}
                    </>
                )}
            </div>

            {open && (
                <div className="ex-panel">
                    <div className="fn-hint ex-panel-hint">
                        После подтверждения заказ автоматически пропадёт из списка{postponed ? "" : overdue ? "" : " (если он в «Просрочено» или «Отложенные»)"}.
                        Если нужно внести несколько оплат сразу — добавьте их кнопкой «+» ниже перед подтверждением.
                    </div>

                    {entries.map((en, idx) => {
                        const st = paymentTypes.find((pt) => pt.id === en.paymentTypeId);
                        const cards = st?.isCard ? kassaList.filter((k) => k.paymentTypeId === en.paymentTypeId && k.showInExpense) : [];
                        const currencyCode = st?.isCard
                            ? (kassaList.find((k) => k.id === en.kassaId)?.currencyCode ?? null)
                            : (st?.currencyCode ?? null);

                        return (
                            <div key={en.id} className="ex-entry">
                                {entries.length > 1 && (
                                    <div className="ex-entry-header">
                                        <span className="fn-hint">Оплата {idx + 1}</span>
                                        <button className="ex-entry-remove" onClick={() => removeEntry(en.id)}>
                                            <i className="fa-solid fa-square-xmark"></i>
                                        </button>
                                    </div>
                                )}

                                <div className="fn-hint">Тип оплаты</div>
                                <div className="fn-paytype">
                                    {paymentTypes.map((pt) => (
                                        <button
                                            key={pt.id}
                                            className={en.paymentTypeId === pt.id ? "selected" : ""}
                                            onClick={() => updateEntry(en.id, { paymentTypeId: pt.id, kassaId: null })}
                                        >
                                            {pt.name}
                                        </button>
                                    ))}
                                </div>

                                {st?.isCard && cards.length > 0 && (
                                    <div className="fn-paytype">
                                        {cards.map((k) => (
                                            <button
                                                key={k.id}
                                                className={en.kassaId === k.id ? "selected" : ""}
                                                onClick={() => updateEntry(en.id, { kassaId: k.id })}
                                            >
                                                {k.recipient}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <div className="fn-field ex-field-amount">
                                    <label>Сумма {currencyCode ? `(${currencyCode})` : ""}</label>
                                    <input
                                        type="number"
                                        placeholder="0.00"
                                        value={en.amount}
                                        onChange={(ev) => updateEntry(en.id, { amount: ev.target.value })}
                                    />
                                </div>
                            </div>
                        );
                    })}

                    <div className="ex-panel-actions">
                        <button className="fn-btn-add-payment btn-confirm-payment" onClick={submit} disabled={submitting}>
                            {submitting ? "Сохраняем…" : "Подтвердить оплату"}
                        </button>
                        <button className="btn-add-entry" onClick={addEntry} disabled={submitting} title="Добавить ещё одну оплату">
                            <i className="fa-solid fa-plus"></i>
                        </button>
                    </div>

                    <button className="btn-cancel-panel" onClick={() => setOpen(false)}>
                        Отмена
                    </button>
                </div>
            )}
        </div>
    );
};

const Expenses = ({ API_URL }) => {
    const navigate = useNavigate();
    const [today, setToday] = useState([]);
    const [overdue, setOverdue] = useState([]);
    const [postponed, setPostponed] = useState([]);
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [kassaList, setKassaList] = useState([]);
    const [loading, setLoading] = useState(true);
    const initialLoadDone = useRef(false);

    const load = useCallback(async () => {
        const scrollY = window.scrollY;
        if (!initialLoadDone.current) setLoading(true);
        try {
            const [dashRes, ptRes, kassaRes] = await Promise.all([
                axios.post(`${API_URL}/getExpensesDashboard`),
                axios.post(`${API_URL}/getPaymentTypes`),
                axios.post(`${API_URL}/getKassaList`),
            ]);
            if (dashRes.data.status === "success") {
                setToday(dashRes.data.today);
                setOverdue(dashRes.data.overdue);
                setPostponed(dashRes.data.postponed || []);
            }
            if (ptRes.data.status === "success") setPaymentTypes(ptRes.data.paymentTypes);
            if (kassaRes.data.status === "success") setKassaList(kassaRes.data.kassaList);
        } catch (err) {
            console.error("load expenses page:", err);
        } finally {
            setLoading(false);
            initialLoadDone.current = true;
            requestAnimationFrame(() => window.scrollTo(0, scrollY));
        }
    }, [API_URL]);

    useEffect(() => { load(); }, [load]);

    const isPaidItem = (e) => e.paidBreakdown && e.paidBreakdown.length > 0;

    const overdueUnpaid = useMemo(
        () => overdue
            .filter((e) => !isPaidItem(e))
            .sort((a, b) => (b.dateEnd || "").localeCompare(a.dateEnd || "")),
        [overdue]
    );
    const paidCount = [...today, ...overdue].filter((e) => e.paidToday).length;
    const pendingTodayCount = today.filter((e) => !isPaidItem(e)).length;
    const visiblePaymentTypes = useMemo(() => paymentTypes.filter((pt) => pt.showInExpense), [paymentTypes]);
    return (
        <div className="finance">
            <div className="fn-topbar">
                <div className="fn-pagetitle">Расходы</div>
            </div>

            <div className="fn-tabs">
                <div className="fn-tab active">Расходы</div>
                <div className="fn-tab" onClick={() => navigate("/expend/kassa")}>Касса</div>
            </div>

            <div className="ex-summary">
                <div className="fn-stat"><div className="fn-lbl">Всего сегодня</div><div className="fn-val">{today.length}</div></div>
                <div className="fn-stat paid"><div className="fn-lbl">Оплачено</div><div className="fn-val">{paidCount}</div></div>
                <div className="fn-stat debt"><div className="fn-lbl">Ожидает оплаты</div><div className="fn-val">{pendingTodayCount}</div></div>
                <div className="fn-stat debt"><div className="fn-lbl">Просрочено</div><div className="fn-val">{overdueUnpaid.length}</div></div>
                <div className="fn-stat"><div className="fn-lbl">Отложено</div><div className="fn-val">{postponed.length}</div></div>
            </div>

           

            {loading ? (
                <div className="fn-empty">Загрузка…</div>
            ) : (
                <>
                    <div className="fn-section-label">Сегодня ({today.length})</div>
                    {today.length === 0 ? (
                        <div className="fn-empty">На сегодня расходов нет</div>
                    ) : (
                        today.map((e, i) => (
                            <ExpenseRow key={`t-${e.orderNumber}-${e.requestType}-${e.role}-${e.personName}-${i}`} e={e} overdue={false} API_URL={API_URL} paymentTypes={visiblePaymentTypes} kassaList={kassaList} onSaved={load} />
                        ))
                    )}

                    {overdueUnpaid.length > 0 && (
                        <>
                            <div className="fn-section-label">Просрочено ({overdueUnpaid.length})</div>
                            {overdueUnpaid.map((e, i) => (
                                <ExpenseRow key={`ov-${e.orderNumber}-${e.requestType}-${e.role}-${e.personName}-${i}`} e={e} overdue={true} API_URL={API_URL} paymentTypes={visiblePaymentTypes} kassaList={kassaList} onSaved={load} />
                            ))}
                        </>
                    )}

                    {postponed.length > 0 && (
                        <>
                            <div className="fn-section-label">Отложенные ({postponed.length})</div>
                            {postponed.map((e, i) => (
                                <ExpenseRow key={`pp-${e.expenseId}-${i}`} e={e} overdue={false} postponed={true} API_URL={API_URL} paymentTypes={visiblePaymentTypes} kassaList={kassaList} onSaved={load} />
                            ))}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default Expenses;